'use client';

import React, { useMemo, useState } from 'react';
import SeasonalPriceInfo from '@/components/excursions/SeasonalPriceInfo';
import Link from 'next/link';
import { Star, Anchor, ChevronRight, ShieldCheck, ShieldAlert, User, Ruler, Users, Heart, BedDouble, Bath, GitCompare } from 'lucide-react';
import { Boat, SearchState } from '@/types';
import BoatImagePlaceholder from '@/components/BoatImagePlaceholder';
import { t } from '@/locales';
import { useBoatTypes } from '@/hooks/useBoatTypes';
import { usePriceRounding } from '@/hooks/usePriceRounding';
import { useCompare } from './CompareContext';

interface BoatListCardProps {
  boat: Boat;
  searchFilters?: SearchState | null;
  isFavorite?: boolean;
  onToggleFavorite?: (boatId: string) => void;
  isUnavailable?: boolean;
  onClickUnavailable?: (boat: Boat) => void;
}

const BoatListCard: React.FC<BoatListCardProps> = ({ boat, searchFilters, isFavorite = false, onToggleFavorite, isUnavailable = false, onClickUnavailable }) => {
  const getDaysCount = () => {
    if (!searchFilters?.startDate || !searchFilters?.endDate) return 1;
    const start = new Date(searchFilters.startDate);
    const end = new Date(searchFilters.endDate);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(1, diff + 1);
  };

  const days = getDaysCount();
  const enriched = boat as Boat & { priceForRangeTotal?: number; priceForRangeAvg?: number; priceForRangeDays?: number };
  const rangeTotal = enriched.priceForRangeTotal;
  const rangeAvg = enriched.priceForRangeAvg;
  const totalPrice = (rangeTotal != null && rangeTotal > 0) ? rangeTotal : boat.price * days;
  // Avg /giorno: priorità a server (priceForRangeAvg), fallback a calc locale.
  const avgDaily = (rangeAvg != null && rangeAvg > 0)
    ? rangeAvg
    : (days > 0 ? Math.round(totalPrice / days) : boat.price);

  // Prezzo "a partire da" secondo rental_mode. price_per_day è SEMPRE giornaliero:
  // weekly → tariffa settimanale (daily-rate ×7), monthly → mensile (×30), daily → /giorno.
  // Col range usiamo avgDaily come base (riflette override/seasonal), esponendo
  // comunque la tariffa di periodo anche per range più corti di una settimana/mese.
  // Senza range: prezzo "da" stagionale (prossima data utile) se presente, altrimenti base.
  const seasonFrom = (boat as { season_from_price?: number | null }).season_from_price;
  // Solo stagionale: senza range usa il prezzo stagionale; se assente (barca dark) → 0 = "su richiesta".
  const dailyRate = (rangeTotal != null && rangeTotal > 0) ? avgDaily : (seasonFrom && seasonFrom > 0 ? seasonFrom : 0);
  const periodMultiplier = boat.rentalMode === 'weekly' ? 7 : boat.rentalMode === 'monthly' ? 30 : 1;
  const displayPrice = dailyRate * periodMultiplier;
  const periodLabel = boat.rentalMode === 'weekly'
    ? t('boatCardExt.perWeek')
    : boat.rentalMode === 'monthly'
      ? t('boatCardExt.perMonth')
      : t('boatCardExt.perDay');

  const { getLabel } = useBoatTypes();
  const { formatPrice } = usePriceRounding();
  const translateBoatType = (type: string) => getLabel(type) || type;

  // Hover-thumb gallery: cicla foto in base a posizione X mouse sull'immagine.
  // Boataround pattern — niente click, niente JS heavy: cycle visivo per scoperta veloce.
  const galleryImgs = (boat.gallery?.length ? boat.gallery : (boat.image ? [boat.image] : [])).slice(0, 5);
  const [hoverIdx, setHoverIdx] = useState(0);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (galleryImgs.length <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const segment = rect.width / galleryImgs.length;
    const idx = Math.min(galleryImgs.length - 1, Math.max(0, Math.floor(x / segment)));
    setHoverIdx(idx);
  };
  const handleMouseLeave = () => setHoverIdx(0);

  const compare = useCompare();
  const inCompare = compare.isSelected(boat.id);
  const handleCompareToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    compare.toggle(boat);
  };

  // Build URL with search params
  const boatUrl = useMemo(() => {
    const base = `/boat/${boat.slug ?? boat.id}`;
    if (!searchFilters) return base;

    const params = new URLSearchParams();
    if (searchFilters.startDate) params.set('startDate', searchFilters.startDate);
    if (searchFilters.endDate) params.set('endDate', searchFilters.endDate);
    if (searchFilters.passengers) params.set('guests', String(searchFilters.passengers));
    if (searchFilters.location) params.set('location', searchFilters.location);

    const queryString = params.toString();
    return queryString ? `${base}?${queryString}` : base;
  }, [boat.id, searchFilters]);

  const handleUnavailableClick = (e: React.MouseEvent) => {
    if (isUnavailable && onClickUnavailable) {
      e.preventDefault();
      onClickUnavailable(boat);
    }
  };

  const Wrapper = isUnavailable && onClickUnavailable ? 'div' : Link;
  const wrapperProps = isUnavailable && onClickUnavailable
    ? { onClick: handleUnavailableClick, role: 'button' as const, tabIndex: 0 }
    : { href: boatUrl };

  return (
    <Wrapper
      {...(wrapperProps as any)}
      data-cid="catalog-list-card"
      data-boat-id={boat.id}
      className={`group bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border transition-all duration-300 flex flex-col sm:flex-row cursor-pointer ${
        isUnavailable
          ? 'border-amber-200 hover:border-amber-300 hover:shadow-lg'
          : 'border-sky-100 hover:border-blue-300 hover:shadow-lg'
      }`}
    >
      {/* Image Container — hover gallery */}
      <div
        className="relative w-full sm:w-72 h-48 sm:h-auto flex-shrink-0 overflow-hidden"
        data-cid="catalog-list-card-image"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {galleryImgs.length > 0 ? (
          galleryImgs.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${boat.name} ${i + 1}`}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
                i === hoverIdx ? 'opacity-100' : 'opacity-0'
              } ${i === hoverIdx ? 'group-hover:scale-105' : ''}`}
              style={{ transition: 'opacity 200ms ease-out, transform 500ms ease' }}
            />
          ))
        ) : (
          <BoatImagePlaceholder className="w-full h-full" />
        )}
        {/* Dots indicator (visibili solo se >1 immagine) */}
        {galleryImgs.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
            {galleryImgs.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === hoverIdx ? 'w-6 bg-white' : 'w-3 bg-white/60'
                }`}
              />
            ))}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20 pointer-events-none" />

        {/* Unavailable badge */}
        {isUnavailable && (
          <div className="absolute bottom-3 left-3 bg-amber-500 text-white px-2.5 py-1 rounded-lg shadow-lg z-10">
            <span className="text-[8px] font-black uppercase tracking-widest">{t('boatCardExt.unavailable')}</span>
          </div>
        )}

        {/* Badges & Favorite */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
          <div className="flex flex-col gap-2">
            {boat.featured && (
              <div className="bg-white/95 backdrop-blur-md px-2.5 py-1 rounded-lg shadow-sm">
                <span className="text-[8px] font-black uppercase tracking-widest text-blue-600">{t('boatCardExt.premium')}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCompareToggle}
              title={inCompare ? 'Rimuovi dal confronto' : 'Aggiungi al confronto'}
              className={`w-9 h-9 rounded-xl backdrop-blur-md transition-all border flex items-center justify-center ${
                inCompare ? 'bg-blue-600 text-white border-blue-600' : 'bg-white/20 text-white border-white/20 hover:bg-white hover:text-blue-600'
              }`}
            >
              <GitCompare className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite?.(boat.id);
              }}
              className={`w-9 h-9 rounded-xl backdrop-blur-md transition-all border border-white/20 flex items-center justify-center group/heart ${
                isFavorite ? 'bg-white text-red-500' : 'bg-white/20 text-white hover:bg-white hover:text-red-500'
              }`}
            >
              <Heart className={`w-4 h-4 transition-transform group-hover/heart:scale-110 ${isFavorite ? 'fill-red-500' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-5 flex flex-col justify-between" data-cid="catalog-list-card-content">
        <div data-cid="catalog-list-card-info">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-3" data-cid="catalog-list-card-header">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">{translateBoatType(boat.type)}</span>
                <span className="w-1 h-1 bg-slate-200 rounded-full" />
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                  <span className="text-[10px] font-bold text-amber-800">{boat.rating}</span>
                  {boat.reviews > 0 && (
                    <span className="text-[10px] text-slate-400">({boat.reviews})</span>
                  )}
                </div>
              </div>
              <h3 className="text-xl font-display font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                {boat.name}
              </h3>
              {/* Brand + charter type chip + New badge */}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {boat.manufacturer && (
                  <span className="text-xs font-semibold text-slate-500">{boat.manufacturer}{boat.model ? ` ${boat.model}` : ''}</span>
                )}
                {boat.skipperRequired ? (
                  <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">Crewed</span>
                ) : boat.skipperAvailable ? (
                  <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">Skipper opz.</span>
                ) : (
                  <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-100">Bareboat</span>
                )}
                {boat.year && boat.year >= new Date().getFullYear() - 2 && (
                  <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">Nuova</span>
                )}
              </div>
            </div>

            {/* License Badge */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-wider ${
              boat.licenseRequired
                ? 'bg-amber-50 text-amber-700 border border-amber-100'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
            }`}>
              {boat.licenseRequired ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
              {boat.licenseRequired ? t('boatCardExt.license') : t('boatCardExt.noLicenseShort')}
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4" data-cid="catalog-list-card-location">
            <div className="flex items-center gap-1.5">
              <Anchor className="w-4 h-4 text-blue-500" />
              <span className="font-medium">{boat.port || boat.location}</span>
            </div>
            {boat.latitude && boat.longitude && (
              <span className="text-[10px] font-mono text-slate-400">
                {boat.latitude.toFixed(4)}°N, {boat.longitude.toFixed(4)}°E
              </span>
            )}
          </div>

          {/* Specs Grid */}
          <div className="flex items-center gap-6 text-sm flex-wrap" data-cid="catalog-list-card-specs">
            <div className="flex items-center gap-2">
              <Ruler className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">
                <span className="font-semibold text-slate-800">{boat.length}m</span> {t('boatCardExt.length').toLowerCase()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">
                <span className="font-semibold text-slate-800">{boat.capacity}</span> {t('boatCardExt.people')}
              </span>
            </div>
            {boat.cabins ? (
              <div className="flex items-center gap-2">
                <BedDouble className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">
                  <span className="font-semibold text-slate-800">{boat.cabins}</span> {t('boatCardExt.cabins').toLowerCase()}
                </span>
              </div>
            ) : null}
            {boat.bathrooms ? (
              <div className="hidden md:flex items-center gap-2">
                <Bath className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">
                  <span className="font-semibold text-slate-800">{boat.bathrooms}</span> {t('boatCardExt.bathrooms').toLowerCase()}
                </span>
              </div>
            ) : null}
            {!boat.cabins && boat.enginePower ? (
              <div className="hidden md:flex items-center gap-2 text-slate-600">
                <span className="font-semibold text-slate-800">{boat.enginePower} CV</span> {t('boatCardExt.engine').toLowerCase()}
              </div>
            ) : null}
          </div>

          {/* Skipper Badge */}
          {boat.skipperAvailable && (
            <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-[11px] font-semibold">
              <User className="w-3.5 h-3.5" />
              {boat.skipperRequired ? t('boatCardExt.skipperIncluded') : t('boatCardExt.skipperAvailable')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-end justify-between mt-4 pt-4 border-t border-slate-100" data-cid="catalog-list-card-footer">
          <div data-cid="catalog-list-card-price">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('boatCardExt.startingFrom')}</p>
            {displayPrice > 0 ? (
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-slate-900">
                  {formatPrice(displayPrice, { withSign: true })}
                </span>
                <span className="text-sm text-slate-400">
                  {periodLabel}
                </span>
              </div>
            ) : (
              <span className="text-sm font-bold text-slate-500">—</span>
            )}
            {days > 1 && searchFilters && (
              <p className="text-sm text-blue-600 font-semibold mt-1">
                {formatPrice(totalPrice, { withSign: true })} {t('boatCardExt.forDays').replace('{days}', String(days))}
              </p>
            )}
            {!(rangeTotal != null && rangeTotal > 0) && (boat as { has_seasons?: boolean }).has_seasons && (
              <SeasonalPriceInfo className="mt-1" />
            )}
          </div>

          <span className={`flex items-center gap-2 px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors group/btn ${
            isUnavailable
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-slate-900 text-white hover:bg-blue-600'
          }`}>
            {isUnavailable ? t('boatCardExt.alternatives') : t('boatCardExt.details')}
            <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

export default BoatListCard;
